<script setup>
import AOS from "aos";
import "aos/dist/aos.css";
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";
import VOtpInput from "vue3-otp-input";
import { useLocalStorage } from "@vueuse/core";
import axios from "axios";
import { useToast } from "vue-toastification";
import {
    initializeClarity,
    initializeHotjar,
    initializeGTM,
} from "@/composables/customThirdPartyPluginHelpers";
import GlobalLayout from "@/Layouts/GlobalLayout.vue";
import { useHead } from "@vueuse/head";

const props = defineProps({
    isOTPBasedLogin: Boolean,
    googleClientId: String,
});

const toast = useToast();

const isOTPBasedLogin = ref(props.isOTPBasedLogin);
const isValidEmail = ref(false);

const email = ref("");
const name = ref(useLocalStorage("name", ""));
const blobImageElement = ref(null);
const password = ref("");
const step = ref(1); // Step 1: Login options, Step 2: Email, Step 3: Password
const isEmailVerifiedFromBackend = ref(false);
const togglePasswordAsText = ref(false);

const otpInputComponent = ref(null);
const otpInputNumber = ref("");
const disableLogin = ref(true);
const timeForRegenerateOTP = ref(119);
let timerCallbackFunction = null;

const formattedTime = computed(() => {
    const minutes = Math.floor(timeForRegenerateOTP.value / 60);
    const seconds = timeForRegenerateOTP.value % 60;
    return `${minutes}m:${seconds}s`;
});

const handleOnComplete = () => {
    disableLogin.value = false;
};

const isValidMail = computed(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.value);
});

const verifyEmail = async () => {
    try {
        const response = await axios.post(route("onboarding.verify_email"), {
            email: email.value,
        }, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        });
        isEmailVerifiedFromBackend.value = response.data.data.isUserExists;
        name.value = response.data.data.name;
        isOTPBasedLogin.value = response.data.data.isOTPBasedLogin;

        if (!response.data?.data?.isUserExists) {
            isValidEmail.value = false;
            toast.error(
                "Invalid email address. Please enter a valid email address."
            );
        } else {
            isValidEmail.value = true;
        }
    } catch (e) {
        toast.error(
            e?.response?.data?.message ||
                "Failed to verify email. Please try again later."
        );
        isEmailVerifiedFromBackend.value = false;
    }
};

const redirectUrl = computed(() => {
    const url = new URL(document.URL);
    return url.searchParams.get("redirect_url");
});

// Remove the automatic email verification watcher for security reasons
// Email will only be verified when user clicks Continue button

const showSection = () => {
    const animatedImage = document.querySelector(".animated-image");
    animatedImage.classList.add("aos-init", "aos-animate");
    animatedImage.setAttribute("data-aos", "fade-up");
    animatedImage.setAttribute("data-aos-delay", "200");
    animatedImage.setAttribute("data-aos-duration", "1200");

    AOS.init();
};

const checkParamsForMessage = () => {
    const url = new URL(document.URL);
    const message = url.searchParams.get("message");
    const status = url.searchParams.get("status");

    if (status === "Failure") {
        toast.error(message);
    } else {
        toast.info(message);
    }

    // Delete the URL Params and update in URL bar
    url.searchParams.delete("message");
    url.searchParams.delete("status");

    window.history.replaceState({}, document.title, url);
};

// Google One Tap state - DISABLED
// const googleButtonElement = ref(null);
// const googleButtonContainer = ref(null);
const emailButtonElement = ref(null);
// const isGoogleScriptLoaded = ref(false);
// const isGoogleInitialized = ref(false);
// const googleButtonWidth = ref(350);
// const googleButtonScale = ref(1);
// let emailButtonObserver = null;

// Load Google Identity Services script - DISABLED
// Calculate Google button width and scale based on email button - DISABLED
// const calculateGoogleButtonWidth = () => {
//     if (!emailButtonElement.value) {
//         console.log("Email button element not found");
//         return;
//     }

//     const emailButton = emailButtonElement.value;
//     const emailButtonWidth = emailButton.offsetWidth;
//     const emailButtonStyles = window.getComputedStyle(emailButton);

//     console.log("Email button measurements:");
//     console.log("- offsetWidth:", emailButtonWidth);
//     console.log("- clientWidth:", emailButton.clientWidth);
//     console.log("- scrollWidth:", emailButton.scrollWidth);
//     console.log("- computed width:", emailButtonStyles.width);
//     console.log(
//         "- padding:",
//         emailButtonStyles.paddingLeft,
//         emailButtonStyles.paddingRight
//     );
//     console.log(
//         "- border:",
//         emailButtonStyles.borderLeftWidth,
//         emailButtonStyles.borderRightWidth
//     );
//     console.log(
//         "- margin:",
//         emailButtonStyles.marginLeft,
//         emailButtonStyles.marginRight
//     );

//     // Google button has a max width of 400px and min width of 200px
//     const targetWidth = emailButtonWidth;
//     const maxGoogleWidth = 400;
//     const minGoogleWidth = 200;

//     // Use the closest valid Google width
//     let googleWidth = Math.min(
//         Math.max(targetWidth, minGoogleWidth),
//         maxGoogleWidth
//     );

//     googleButtonWidth.value = googleWidth;
// };

// Set up ResizeObserver to watch email button size changes - DISABLED
// const setupEmailButtonObserver = () => {
//     if (!emailButtonElement.value || emailButtonObserver) {
//         return;
//     }

//     emailButtonObserver = new ResizeObserver((entries) => {
//         for (const entry of entries) {
//             console.log("Email button size changed:", entry.contentRect.width);
//             calculateGoogleButtonWidth();
//         }
//     });

//     emailButtonObserver.observe(emailButtonElement.value);
//     console.log("ResizeObserver set up for email button");
// };

// Load Google Identity Services script - DISABLED
// const loadGoogleScript = () => {
//     return new Promise((resolve, reject) => {
//         if (isGoogleScriptLoaded.value) {
//             resolve();
//             return;
//         }

//         const script = document.createElement("script");
//         script.src = "https://accounts.google.com/gsi/client";
//         script.async = true;
//         script.defer = true;

//         script.onload = () => {
//             isGoogleScriptLoaded.value = true;
//             resolve();
//         };

//         script.onerror = reject;
//         document.head.appendChild(script);
//     });
// };

// Function to handle Google One Tap response - DISABLED
// const handleGoogleOneTapResponse = async (response) => {
//     console.log("Google One Tap response received:", response);

//     if (!response.credential) {
//         console.error("No credential in response");
//         return;
//     }

//     try {
//         const result = await axios.post("/auth/google/onetap", {
//             credential: response.credential,
//         });

//         console.log("Backend response:", result.data);

//         if (result.data.success && result.data.redirect_url) {
//             window.location.href = result.data.redirect_url;
//         } else {
//             console.error("Login failed:", result.data);
//         }
//     } catch (error) {
//         console.error("Error during Google login:", error);
//     }
// };

// Initialize Google One Tap - DISABLED
// const initializeGoogleOneTap = async () => {
//     if (isGoogleInitialized.value) {
//         console.log("Google One Tap already initialized");
//         return;
//     }

//     if (!props.googleClientId || !googleButtonElement.value) {
//         console.error("Missing requirements:", {
//             googleClientId: props.googleClientId,
//             buttonElement: googleButtonElement.value,
//         });
//         return;
//     }

//     console.log("Loading Google script...");
//     await loadGoogleScript();
//     console.log("Google script loaded successfully");

//     console.log(
//         "Initializing Google One Tap with client ID:",
//         props.googleClientId
//     );

//     // Initialize Google One Tap
//     window.google.accounts.id.initialize({
//         client_id: props.googleClientId,
//         callback: handleGoogleOneTapResponse,
//         auto_select: false,
//         cancel_on_tap_outside: false,
//     });

//     console.log("Setting up email button observer...");
//     setupEmailButtonObserver();

//     console.log("Calculating button width...");
//     calculateGoogleButtonWidth();

//     // Render the sign-in button with calculated width
//     window.google.accounts.id.renderButton(googleButtonElement.value, {
//         theme: "outline",
//         size: "large",
//         type: "standard",
//         text: "signin_with",
//         shape: "rectangular",
//         logo_alignment: "center",
//         width: googleButtonWidth.value,
//     });

//     // Display the One Tap prompt
//     window.google.accounts.id.prompt((notification) => {
//         console.log("One Tap prompt notification:", notification);
//         if (notification.isNotDisplayed()) {
//             console.log(
//                 "One Tap not displayed:",
//                 notification.getNotDisplayedReason()
//             );
//         } else if (notification.isSkippedMoment()) {
//             console.log("One Tap skipped:", notification.getSkippedReason());
//         }
//     });

//     // Mark as initialized
//     isGoogleInitialized.value = true;
//     console.log("Google One Tap initialization complete");
// };

// Register the callback function globally so Google One Tap can access it - DISABLED
// window.handleGoogleOneTapResponse = handleGoogleOneTapResponse;

// Watch for when both the button element and client ID are available - DISABLED
// watch(
//     [googleButtonElement, emailButtonElement, () => props.googleClientId, step],
//     ([buttonEl, emailButtonEl, clientId, currentStep]) => {
//         console.log("Watcher triggered:", {
//             buttonEl,
//             emailButtonEl,
//             clientId,
//             currentStep,
//         });

//         if (!buttonEl || !emailButtonEl || !clientId || currentStep !== 1) {
//             console.log("Requirements not met yet:", {
//                 hasButton: !!buttonEl,
//                 hasEmailButton: !!emailButtonEl,
//                 hasClientId: !!clientId,
//                 isStep1: currentStep === 1,
//             });
//             return;
//         }

//         console.log("Initializing Google One Tap...");
//         initializeGoogleOneTap();
//     },
//     { immediate: true }
// );

onMounted(() => {
    document.body.classList.add("full-login-page");
    initializeClarity();
    initializeHotjar();
    initializeGTM();
    AOS.init();
    const image = blobImageElement.value;
    if (image.complete) {
        showSection();
    } else {
        image.addEventListener("load", showSection);
    }

    checkParamsForMessage();

    // Add resize event listener for Google button - DISABLED
    // window.addEventListener("resize", handleResize);

    // Debug props - DISABLED
    // console.log("Component mounted with props:", {
    //     isOTPBasedLogin: props.isOTPBasedLogin,
    //     googleClientId: props.googleClientId,
    // });
    // console.log("googleButtonElement ref:", googleButtonElement.value);
});

onBeforeUnmount(() => {
    document.body.classList.remove("full-login-page");
    // Remove resize event listener - DISABLED
    // window.removeEventListener("resize", handleResize);
    // Clear any pending resize timeout - DISABLED
    // if (resizeTimeout) {
    //     clearTimeout(resizeTimeout);
    // }
    // Clean up ResizeObserver - DISABLED
    // if (emailButtonObserver) {
    //     emailButtonObserver.disconnect();
    //     emailButtonObserver = null;
    // }
});

const login = async () => {
    try {
        if (isOTPBasedLogin.value) {
            if (otpInputComponent.value.otp.length < 6) {
                toast.warning("Please fill all the digits");
                return;
            }

            // Login with OTP
            let response = await axios.post(route("onboarding.verify_otp"), {
                email: email.value,
                otp: otpInputNumber.value,
            });
            toast.success("OTP Verified successfully");

            if (redirectUrl.value) {
                window.location.href = redirectUrl.value;
            } else {
                window.location.href = `${
                    response.data?.redirect_to ??
                    route("onboarding.account_type")
                }?isOnBoardingUser=1`;
            }
        } else {
            // Login with email and password
            const response = await axios.post("/login", {
                email: email.value,
                password: password.value,
            });

            if (redirectUrl.value) {
                window.location.href = redirectUrl.value;
            } else {
                window.location.href = `${
                    response.data?.redirect_to ??
                    route("onboarding.account_type")
                }`;
            }
        }
    } catch (e) {
        toast.error(
            e?.response?.data?.message ||
                "Failed to login. Please try again later"
        );
    }
};

const continueWithEmail = () => {
    step.value = 2; // Go to email input step
};

// Re-render Google button with new width - DISABLED
// const reRenderGoogleButton = async () => {
//     if (
//         !isGoogleInitialized.value ||
//         !googleButtonElement.value ||
//         !window.google
//     ) {
//         console.log("Cannot re-render: not initialized or missing elements");
//         return;
//     }

//     console.log("Re-rendering Google button...");

//     // Clear the existing button
//     googleButtonElement.value.innerHTML = "";

//     // Calculate new width
//     calculateGoogleButtonWidth();

//     // Re-render with new width (wrapper controls alignment)
//     window.google.accounts.id.renderButton(googleButtonElement.value, {
//         theme: "outline",
//         size: "large",
//         type: "standard",
//         text: "signin_with",
//         shape: "rectangular",
//         logo_alignment: "center",
//         // Remove width parameter - let wrapper control alignment
//     });
// };

// Debounce function to limit resize events - DISABLED
// let resizeTimeout = null;

// Handle window resize with debouncing - DISABLED
// const handleResize = () => {
//     console.log("Window resize detected...");

//     // Clear existing timeout
//     if (resizeTimeout) {
//         clearTimeout(resizeTimeout);
//     }

//     // Set new timeout to debounce resize events
//     resizeTimeout = setTimeout(() => {
//         console.log("Executing debounced resize, updating Google button...");
//         reRenderGoogleButton();
//     }, 300); // Wait 300ms after last resize event
// };

const goToNextStep = async () => {
    if (!isValidMail.value) {
        toast.error("Please enter a valid email address.");
        return;
    }

    // Verify email when user clicks Continue
    await verifyEmail();

    // Check if user exists before proceeding
    if (!isEmailVerifiedFromBackend.value) {
        // Error toast is already shown in verifyEmail function
        return;
    }

    step.value = 3; // Go to password step

    if (isOTPBasedLogin.value) {
        await regenerateOTP();
    }
};

const regenerateOTP = async () => {
    try {
        const response = await axios.post(route("onboarding.generate_otp"), {
            email: email.value,
        });
        timeForRegenerateOTP.value = 119;

        timerCallbackFunction = setInterval(() => {
            if (timeForRegenerateOTP.value > 0) {
                timeForRegenerateOTP.value--;
            } else {
                clearInterval(timerCallbackFunction);
            }
        }, 1000);

        toast.success("OTP sent. Please check your inbox.");
    } catch (e) {
        toast.error("Failed to send OTP. Please try again later.");
    }
};
</script>

<template>
    <GlobalLayout>
        <div
            class="p-6 lg:p-12 flex flex-col items-center justify-center h-full min-h-screen"
        >
            <div
                class="flex flex-col-reverse lg:grid grid-cols-8 gap-8 lg:gap-4 items-center h-full max-w-screen-2xl mx-auto"
            >
                <form @submit.prevent="login" class="col-span-3 w-full">
                    <img
                        alt="Brandwell Logo"
                        src="/assets/images/v4/logo.png"
                        class="max-w-40 w-full mb-12"
                    />
                    <header
                        v-if="isOTPBasedLogin && step === 3 && isValidEmail"
                        class="flex px-30 flex-col justify-center items-start gap-7 font-['Nunito'] w-10/12"
                    >
                        <h1
                            class="text-4xl text-white font-semibold leading-10"
                        >
                            Enter your code
                        </h1>

                        <p class="text-base font-normal text-white leading-6">
                            We sent a one-time code to your email<span
                                class="text-[#9F62FF] mx-2"
                                >{{ email }}</span
                            >You can check your inbox.
                        </p>

                        <!--                OTP -->
                        <div class="w-full text-white">
                            <div class="flex gap-8 justify-start">
                                <v-otp-input
                                    ref="otpInputComponent"
                                    input-classes="otp-input"
                                    :conditionalClass="[
                                        'one',
                                        'two',
                                        'three',
                                        'four',
                                        'five',
                                        'six',
                                    ]"
                                    separator=""
                                    inputType="number"
                                    inputmode="numeric"
                                    :num-inputs="6"
                                    v-model:value="otpInputNumber"
                                    :should-auto-focus="true"
                                    :should-focus-order="true"
                                    @on-complete="handleOnComplete"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            class="next-step aos-init flex w-full h-[41px] p-[10px_25px_10px_30px] justify-center items-center gap-[5px] rounded-[4px] border border-solid border-[#9E2AB9] bg-[linear-gradient(98deg,_#7625E0_3.68%,_#ED336A_102.8%)] text-[0.875rem] text-white"
                            :disabled="disableLogin"
                        >
                            Login
                        </button>

                        <span class="text-[#D7CEE2]"
                            >Didn't receive the email
                            <button
                                @click="regenerateOTP"
                                :disabled="timeForRegenerateOTP"
                                class="text-[#9F62FF] mx-1"
                                :class="{
                                    'opacity-50': timeForRegenerateOTP,
                                }"
                                type="button"
                            >
                                Click here
                            </button>
                            to get another in {{ formattedTime }}
                        </span>
                    </header>

                    <div v-else class="flex flex-col items-baseline p-0">
                        <div class="flex items-start max-w-full">
                            <h2 class="welcome-msg truncate">
                                Welcome back{{ name ? ' ' + name : '' }}...
                            </h2>
                        </div>

                        <div class="w-full">
                            <!-- Step 1: Login Options -->
                            <div
                                v-if="step === 1"
                                class="w-full flex flex-col items-start gap-4"
                            >
                                <div class="mb-[1.688rem]">
                                    <label
                                        class="aos-init text-sm lg:text-base text-gray-300"
                                        data-aos="fade-up"
                                        data-aos-delay="600"
                                        data-aos-duration="1200"
                                        >How would you like to login?</label
                                    >
                                </div>

                                <!-- Google Sign-in Button - DISABLED -->
                                <!-- <div class="min-w-[200px]">
                                    <div class="google-button-wrapper">
                                        <div ref="googleButtonElement"></div>
                                    </div>
                                </div> -->

                                <!-- Continue with Email Button -->
                                <div
                                    ref="emailButtonElement"
                                    class="relative w-fit min-w-[200px]"
                                >
                                    <div
                                        class="absolute inset-0 rounded-[6px] p-[1px] bg-gradient-to-r from-[#7625E0] to-[#ED336A]"
                                    >
                                        <div
                                            class="h-full rounded-[5px] bg-[#1a0b2e]"
                                        ></div>
                                    </div>
                                    <button
                                        type="button"
                                        @click="continueWithEmail"
                                        class="relative flex py-3 px-4 justify-center items-center gap-2 rounded-md bg-transparent text-white text-sm font-medium hover:bg-gradient-to-r hover:from-[rgba(118,37,224,0.1)] hover:to-[rgba(237,51,106,0.1)] transition-all duration-200"
                                    >
                                        <svg
                                            class="w-5 h-5"
                                            fill="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                                            />
                                        </svg>
                                        Continue with email
                                    </button>
                                </div>

                                <span
                                    class="text-[#9F62FF] text-sm lg:text-base text-center block mt-12"
                                    >Don't have an account yet?
                                    <a
                                        class="text-[#EC3269]"
                                        href="https://brandwell.ai/pricing?from=login"
                                        >Sign up</a
                                    >
                                </span>
                            </div>

                            <!-- Step 2: Email Input -->
                            <div
                                v-if="step === 2"
                                class="mb-[1.688rem] flex flex-col gap-[7px] relative"
                            >
                                <label
                                    class="aos-init text-[0.75rem] text-white"
                                    data-aos="fade-up"
                                    data-aos-delay="600"
                                    data-aos-duration="1200"
                                    >Please enter your work email</label
                                >
                                <div
                                    class="rounded-[4px] h-[46px] p-[1px] relative shadow-[0px_2px_4px_-2px_rgba(16,_24,_40,_0.06),_0px_4px_8px_-2px_rgba(16,_24,_40,_0.1)] bg-[linear-gradient(98deg,_#7625E0_3.68%_,_#ED336A_102.8%)] focus-within:bg-[linear-gradient(98deg,_#EC3269_3.68%_,_#EC3269_102.8%)] aos-init w-full max-w-96"
                                    data-aos="zoom-out"
                                    data-aos-delay="1300"
                                    data-aos-duration="500"
                                >
                                    <input
                                        type="email"
                                        v-model.lazy="email"
                                        name="email"
                                        class="email email-input1 border border-solid border-transparent rounded-[4px] h-full p-[1rem] text-[0.8125rem] w-full bg-[#1d0937] text-white"
                                        placeholder="example@company.com"
                                    />
                                    <img
                                        src="/assets/images/v4/email-validate.svg"
                                        v-show="isValidMail"
                                        class="validate-icon email-status1 absolute w-[16px] right-[14px] top-[14px]"
                                        alt=""
                                    />
                                </div>
                                <span
                                    class="error text-[#EC3269] text-[0.75rem]"
                                    style="display: none"
                                    >Oops, these credentials do not match our
                                    records. Please check.</span
                                >
                            </div>

                            <div v-if="step === 2">
                                <button
                                    type="button"
                                    class="next-step aos-init flex w-[194px] h-[41px] p-[10px_25px_10px_30px] justify-center items-center gap-[5px] rounded-[4px] border border-solid border-[#9E2AB9] bg-[linear-gradient(98deg,_#7625E0_3.68%,_#ED336A_102.8%)] text-[0.875rem] mb-[1.188rem] text-white"
                                    data-aos="zoom-out"
                                    data-aos-delay="1500"
                                    data-aos-duration="500"
                                    @click="goToNextStep"
                                    :disabled="!isValidMail"
                                >
                                    Continue
                                </button>

                                <span
                                    class="text-[#9F62FF] text-[0.75rem]"
                                    data-aos="zoom-out"
                                    data-aos-delay="1700"
                                    data-aos-duration="500"
                                    >Don't have an account yet?
                                    <a
                                        class="text-[#EC3269]"
                                        href="https://brandwell.ai/pricing?from=login"
                                        >Sign up</a
                                    >
                                </span>
                            </div>

                            <div v-if="step === 3">
                                <div
                                    class="mb-[1.688rem] flex flex-col gap-[7px] relative"
                                >
                                    <label class="text-[0.75rem] text-white"
                                        >Password</label
                                    >
                                    <div
                                        class="rounded-[4px] h-[46px] p-[1px] relative shadow-[0px_2px_4px_-2px_rgba(16,_24,_40,_0.06),_0px_4px_8px_-2px_rgba(16,_24,_40,_0.1)] bg-[linear-gradient(98deg,_#7625E0_3.68%_,_#ED336A_102.8%)] focus-within:bg-[linear-gradient(98deg,_#EC3269_3.68%_,_#EC3269_102.8%)]"
                                    >
                                        <input
                                            :type="
                                                togglePasswordAsText
                                                    ? 'text'
                                                    : 'password'
                                            "
                                            name="password"
                                            placeholder=""
                                            class="border border-solid border-transparent rounded-[4px] h-full p-[1rem] text-[0.8125rem] w-full bg-[#1d0937] text-white"
                                            v-model="password"
                                        />

                                        <a
                                            href="javascript:void(0)"
                                            @click="
                                                togglePasswordAsText =
                                                    !togglePasswordAsText
                                            "
                                            class="absolute right-[13px] top-[13px] w-[19px]"
                                        >
                                            <img
                                                src="/assets/images/v4/show.svg"
                                                :class="
                                                    togglePasswordAsText
                                                        ? 'hidden'
                                                        : 'block'
                                                "
                                                alt=""
                                            />
                                            <img
                                                src="/assets/images/v4/hide.svg"
                                                :class="
                                                    togglePasswordAsText
                                                        ? 'block'
                                                        : 'hidden'
                                                "
                                                alt=""
                                            />
                                            <i
                                                :class="
                                                    togglePasswordAsText
                                                        ? 'bx-hide'
                                                        : 'bx-show'
                                                "
                                            ></i>
                                        </a>
                                    </div>
                                </div>

                                <div
                                    class="flex items-center justify-between text-[#9F62FF] text-[0.75rem] mb-[1.5rem]"
                                >
                                    <div class="flex">
                                        <label class="checkbox-container">
                                            <input type="checkbox" />
                                            <span class="checkmark"></span>
                                            Keep me logged in
                                        </label>
                                    </div>
                                    <a
                                        :href="`${route(
                                            'v4.password.reset'
                                        )}?email=${email}`"
                                        class="text-[#9F62FF] text-[0.75rem]"
                                    >
                                        Forgot Password?
                                    </a>
                                </div>

                                <div>
                                    <button
                                        type="submit"
                                        class="w-full flex h-[41px] p-[10px_25px_10px_30px] justify-center items-center gap-[5px] rounded-[4px] border border-solid border-[#9E2AB9] bg-[linear-gradient(98deg,_#7625E0_3.68%_,_#ED336A_102.8%)] text-white text-[0.875rem] mb-[1.188rem]"
                                    >
                                        LOG IN
                                    </button>

                                    <a
                                        href="https://help.brandwell.ai/en/"
                                        class="text-[#D7CEE2] opacity-[0.5] text-[0.75rem]"
                                        >Having trouble? Visit our help
                                        center</a
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <div class="col-span-4 col-start-5">
                    <div class="animated-image px-5">
                        <img
                            src="/assets/images/v4/glow-img.png"
                            class="bg-img"
                            ref="blobImageElement"
                            alt=""
                        />
                        <img
                            src="/assets/images/v4/glow-circle.svg"
                            class="circle-glow"
                            alt=""
                        />
                        <h2>
                            What do you <br />
                            want me to <br />
                            write?
                        </h2>
                    </div>
                </div>
            </div>
        </div>
    </GlobalLayout>
</template>

<link href="https://unpkg.com/aos@2.3.1/dist/aos.css" rel="stylesheet" />

<style lang="scss">
.full-login-page {
    background: linear-gradient(
        144deg,
        #000 4.85%,
        #1c003b 30.68%,
        #1e023c 76.88%,
        #200028 88.35%
    );
    padding-top: 0;
}

// Google button container styling
#googleButtonElement,
[ref="googleButtonElement"] {
    width: 100% !important;
    height: 50px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
}

h2.welcome-msg {
    font-size: clamp(2rem, 2.5vw, 3rem);
    color: #fff;
    -webkit-animation: type3 1s steps(40, end);
    animation: type3 1s steps(40, end);
    -webkit-animation-fill-mode: forwards;
    animation-fill-mode: forwards;
    white-space: nowrap;
    overflow: hidden;

    &#welcome-msg-legnth {
        width: auto;
        animation: unset;
        opacity: 0;
        border: 0;
        position: absolute;
    }
}

@keyframes type3 {
    from {
        width: 0;
    }
    to {
        width: 100%;
    }
}

@keyframes blink2 {
    50% {
        border-color: transparent;
    }
}

@-webkit-keyframes blink2 {
    50% {
        border-color: tranparent;
    }
}

.animated-image {
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0 !important;

    @media (width >= 1024px) {
        margin-top: -84px;
    }

    &.aos-animate {
        opacity: 1 !important;
    }

    .bg-img {
        width: 500px;

        @media (width >= 1024px) {
            width: 761px;
        }
    }

    img.circle-glow {
        width: 250px;
        position: absolute;
        animation: spin infinite 3s linear;

        @media (width >= 1024px) {
            width: 352px;
        }
    }

    h2 {
        position: absolute;
        font-size: 1.146rem;
        color: #fff;
        text-transform: uppercase;
        text-align: center;
        line-height: 1.563rem;
        letter-spacing: 1.834px;
    }

    &.aos-animate {
        opacity: 1 !important;
    }
}

.checkbox-container {
    display: inline-block;
    position: relative;
    padding-left: 30px;
    cursor: pointer;
    font-size: 0.75rem;
}

.checkbox-container input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
}

.checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 18px;
    width: 18px;
    background-color: transparent;
    border: 1px solid #9f62ff;
    border-radius: 3px;
}

.checkbox-container:hover input ~ .checkmark {
    background-color: rgba(159, 98, 255, 0.1);
}

.checkbox-container input:checked ~ .checkmark {
    background-color: rgba(159, 98, 255, 0.5);
}

.checkmark:after {
    content: "";
    position: absolute;
    display: none;
}

.checkbox-container input:checked ~ .checkmark:after {
    display: block;
}

.checkbox-container .checkmark:after {
    left: 6px;
    top: 3px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
}

[type="text"]:focus,
[type="email"]:focus,
[type="password"]:focus {
    outline: unset;
    box-shadow: unset;
    border-color: transparent;
}

.otp-input {
    width: 40px;
    height: 48px;
    padding: 5px;
    font-size: 20px;
    margin: 0 10px;
    border-radius: 4px;
    text-align: center;
    border: 1px solid #a42bb4;
    background: #1c0538;
    box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.1),
        0px 2px 4px -2px rgba(16, 24, 40, 0.06);
}

.otp-input:focus {
    border-color: #9e2ab9;
    outline: none;
    --tw-ring-color: none;
}

/* Background colour of an input field with value */
.otp-input.is-complete {
    background-color: transparent;
}

.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.opt-input input::placeholder {
    font-size: 15px;
    text-align: center;
    font-weight: 600;
}

.otp-input-container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
}

.one {
    margin-left: 0;
}

/* Google button wrapper styling */
.google-button-wrapper {
    overflow: hidden;
    transition: transform 0.2s ease;
}
</style>
